// El dashboard del Practicante recibe los proyectos terminados por los técnicos para preparar los entregables finales para el cliente.
import React, { useState, useEffect } from 'react';
import { 
    collection, query, where, doc, updateDoc, onSnapshot, 
    deleteField, Timestamp} from 'firebase/firestore';

import { 
    ref, uploadBytesResumable, getDownloadURL 
} from 'firebase/storage';
import { db, storage } from '../firebase/config'
import 'react-toastify/dist/ReactToastify.css';
import { ConfirmationModal } from '../components/UI/ConfirmationModal'
import { PROYECTOS_COLLECTION} from '../utils/constants'

export const PracticanteDashboard = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalProject, setModalProject] = useState(null);
    const [submittingId, setSubmittingId] = useState(null);
    const [confirmingAction, setConfirmingAction] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchProjects = () => {
        setLoading(true);
        const q = query(collection(db, PROYECTOS_COLLECTION), where("estado", "in", ["Terminado Internamente", "En Revisión Final"]));
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setProjects(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            console.error("Error fetching projects for practicante: ", error);
            setLoading(false);
        });
        
        return unsubscribe;
    };

    useEffect(() => {
        const unsubscribe = fetchProjects();
        return () => unsubscribe();
    }, []);

    const sortedProjects = React.useMemo(() => {
        const filtered = projects.filter(p => 
            (p.npu?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.clienteNombre?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.servicioNombre?.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const sortable = [...filtered];
        sortable.sort((a, b) => {
            const dateA = a.fase2_fechaFinTecnico || a.fase1_fechaFinTecnico;
            const dateB = b.fase2_fechaFinTecnico || b.fase1_fechaFinTecnico;
            const fieldA = dateA?.toDate() || new Date(0);
            const fieldB = dateB?.toDate() || new Date(0);
            if (fieldA < fieldB) return sortOrder === 'asc' ? -1 : 1;
            if (fieldA > fieldB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return sortable;
    }, [projects, sortOrder, searchTerm]);

    const currentItems = sortedProjects.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const totalPages = Math.ceil(sortedProjects.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleSendToReview = async (projectId) => {
        setSubmittingId(projectId);
        try {
            await updateDoc(doc(db, PROYECTOS_COLLECTION, projectId), {
                estado: 'En Revisión Final',
                motivoRechazo: deleteField()
            });
        } catch (error) {
            console.error("Error sending project to review:", error);
            alert("Ocurrió un error al enviar el proyecto a revisión.");
        } finally {
            setSubmittingId(null);
        }
    };

    const promptSendToReview = (projectId) => {
        setConfirmingAction({
            title: "Confirmar Envío",
            message: "¿Estás seguro de que todos los documentos están listos y quieres enviar este proyecto a revisión final?",
            onConfirm: () => {
                handleSendToReview(projectId);
                setConfirmingAction(null);
            }
        });
    };

    const ManageFinalDeliveryModal = ({ project, onClose, onFinalized }) => {
        const [heyzineUrl, setHeyzineUrl] = useState(project.urlHeyzine || '');
        const [notaPdfFile1, setNotaPdfFile1] = useState(null);
        const [notaPdfFile2, setNotaPdfFile2] = useState(null);
        const [fechaEmision, setFechaEmision] = useState('');
        const [vigenciaAnos, setVigenciaAnos] = useState('');
        const [loading, setLoading] = useState(false);

        const handleFileChange = (setter) => (e) => { if (e.target.files[0]) setter(e.target.files[0]) };

        const uploadFile = async (file, path) => {
            if (!file) return null;
            const storageRef = ref(storage, path);
            const uploadTask = uploadBytesResumable(storageRef, file);
            return getDownloadURL((await uploadTask).ref);
        };

        const handleSave = async () => {
            setLoading(true);
            const updatePayload = {
                urlHeyzine: heyzineUrl 
            };

            if (fechaEmision) {
                const dateObj = new Date(fechaEmision + 'T12:00:00');
                updatePayload.fechaEmision = Timestamp.fromDate(dateObj);
            }

            if (vigenciaAnos !== '') {
                updatePayload.vigenciaAnos = Number(vigenciaAnos);
            }

            const nota1Url = await uploadFile(notaPdfFile1, `notas_entrega_cliente/${project.id}/nota1_${notaPdfFile1?.name}`);
            const nota2Url = await uploadFile(notaPdfFile2, `notas_entrega_cliente/${project.id}/nota2_${notaPdfFile2?.name}`);

            if (nota1Url) updatePayload.urlNotaPdf1 = nota1Url;
            if (nota2Url) updatePayload.urlNotaPdf2 = nota2Url;

            if (Object.keys(updatePayload).length > 0) {
                await updateDoc(doc(db, PROYECTOS_COLLECTION, project.id), updatePayload);
            }
            onFinalized();
            onClose();
        };

        const showVigenciaFields = project.fechaEmision === undefined || project.vigenciaAnos === undefined;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                    <h3 className="text-lg font-bold mb-4">Gestionar Entrega Final</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Enlace Heyzine</label>
                            <input type="text" value={heyzineUrl} onChange={(e) => setHeyzineUrl(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"/>
                        </div>

                        <hr className="border-gray-200"/>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Subir Nota de Entrega Preliminar</label>
                            <input type="file" accept=".pdf" onChange={handleFileChange(setNotaPdfFile1)} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-800 hover:file:bg-blue-100"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Subir Nota de Entrega Final</label>
                            <input type="file" accept=".pdf" onChange={handleFileChange(setNotaPdfFile2)} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-800 hover:file:bg-blue-100"/>
                        </div>

                        {showVigenciaFields && (
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                                <h4 className="text-sm font-bold text-blue-800 mb-3">Datos de Vigencia del Estudio</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Fecha de Emisión</label>
                                        <input 
                                            type="date" 
                                            value={fechaEmision} 
                                            onChange={(e) => setFechaEmision(e.target.value)} 
                                            className="block w-full text-sm border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <p className="text-[10px] text-gray-500 mt-1">Ajustar la fecha en caso de ser necesario</p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Vigencia (Años)</label>
                                        <input 
                                            type="number" 
                                            min="0"
                                            placeholder="Ej. 0, 1, o 2"
                                            value={vigenciaAnos} 
                                            onChange={(e) => setVigenciaAnos(e.target.value)} 
                                            className="block w-full text-sm border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <p className="text-[10px] text-gray-500 mt-1">Usa "0" para si el estudio no tiene vigencia</p>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                    <div className="mt-6 flex justify-end space-x-3">
                        <button onClick={onClose} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded hover:bg-gray-300 transition-colors">Cancelar</button>
                        <button onClick={handleSave} disabled={loading} className="bg-blue-600 text-white font-bold py-2 px-4 rounded hover:bg-blue-700 transition-colors disabled:opacity-50">
                            {loading ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Proyectos Listos para Documentar</h1>
                <div className="flex items-center space-x-4">
                    <input 
                        type="text"
                        placeholder="Buscar por NPU, Cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <div className="flex items-center">
                        <span className="text-sm mr-2">Mostrar:</span>
                        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-2 py-1 border border-gray-300 rounded-md">
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium">Ordenar por Fecha:</span>
                        <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} className="p-2 border rounded-md text-sm bg-white shadow-sm">
                            {sortOrder === 'asc' ? 'Ascendente ↑' : 'Descendente ↓'}
                        </button>
                    </div>
                </div>
            </div>

            {loading ? <p>Cargando...</p> : sortedProjects.length === 0 ? <p>No hay proyectos pendientes.</p> : (
                <>
                    <div className="overflow-x-auto bg-white rounded-lg shadow">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">NPU</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documentos y correcciones</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                    {currentItems.map(project => {
                                    const statusClass = project.estado === 'En Revisión Final' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
                                    return (
                                        <tr key={project.id} className={project.motivoRechazo ? "bg-orange-50" : ""}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{project.npu}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{project.clienteNombre}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">{project.servicioNombre}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}`}>
                                                    {project.estado}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col space-y-2 text-sm">
                                                    {project.fase1_urlEvidencia && (
                                                        <div>
                                                            <p className="font-semibold text-xs">Fase 1:</p>
                                                            <a href={project.fase1_urlEvidencia} target="_blank" rel="noopener noreferrer" className="text-blue-600">Ver Evidencia 1</a>
                                                            <p>Nota Interna: <span className="font-semibold">{project.fase1_numeroNotaInterna || 'N/A'}</span></p>
                                                        </div>
                                                    )}
                                                    {project.fase2_urlEvidencia && (
                                                        <div>
                                                            <p className="font-semibold text-xs">Fase 2:</p>
                                                            <a href={project.fase2_urlEvidencia} target="_blank" rel="noopener noreferrer" className="text-blue-600">Ver Evidencia 2</a>
                                                            <p>Nota Interna: <span className="font-semibold">{project.fase2_numeroNotaInterna || 'N/A'}</span></p>
                                                        </div>
                                                    )}
                                                    {project.motivoRechazo && (
                                                        <div className="mt-2 p-2 bg-red-100 text-red-800 rounded-md text-xs border border-red-200">
                                                            <p className="font-bold">Requiere Corrección:</p>
                                                            <p>{project.motivoRechazo}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex items-center space-x-4">
                                                    <button onClick={() => setModalProject(project)} className="text-indigo-600 hover:text-indigo-800">Gestionar Entrega</button>
                                                    {project.estado === 'Terminado Internamente' ? (
                                                        <button onClick={() => promptSendToReview(project.id)} disabled={submittingId === project.id} className="text-green-600 hover:text-green-800 disabled:opacity-50">
                                                            {submittingId === project.id ? 'Enviando...' : 'Enviar a Revisión'}
                                                        </button>
                                                    ) : (
                                                        <span className="text-sm font-semibold text-gray-500">Enviado</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex justify-between items-center">
                        <span className="text-sm text-gray-700">Página {currentPage} de {totalPages}</span>
                        <div>
                            <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md bg-white mr-2 disabled:opacity-50">Anterior</button>
                            <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 border rounded-md bg-white disabled:opacity-50">Siguiente</button>
                        </div>
                    </div>
                </>
            )}
            {modalProject && <ManageFinalDeliveryModal project={modalProject} onClose={() => setModalProject(null)} onFinalized={fetchProjects} />}
            {confirmingAction && <ConfirmationModal {...confirmingAction} onCancel={() => setConfirmingAction(null)} />}
        </div>
    );
};